"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { Header } from "@/modules/shared/components/Header";
import { Footer } from "@/modules/shared/components/Footer";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useSettings } from "@/modules/shared/hooks/useSettings";
import { toBionicHtml } from "@/modules/shared/utils/bionic";

interface Author {
  nombre: string;
  titulo: string;
  avatar: string;
  enlace: string;
}

interface Article {
  id: string;
  categoriaId: string;
  fecha: string;
  fechaFormateada: string;
  lecturaMin: string;
  titulo: string;
  tituloLargo: string;
  extracto: string;
  likes: number;
  reads: number;
  bookmarks: number;
  autor: Author;
  estado: "publicado" | "proximamente";
  contenidoGratis: string;
  contenidoPremium: string;
}

export default function ArticuloClient({ id }: { id: string }) {
  const { user, loginWithGoogle } = useAuth();
  const { preferences } = useSettings();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/data/revista.json")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.articulos) {
          const found = data.articulos.find((art: any) => art.id === id);
          if (found) {
            setArticle(found);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching article details:", err);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-bg-main flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
        </main>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-bg-main pt-36 pb-20 text-center">
          <h2 className="font-serif text-2xl text-text-pure mb-4">Artículo no encontrado</h2>
          <p className="text-text-muted mb-8">El recurso solicitado no está disponible.</p>
          <Link href="/articulos" className="btn-gold-solid px-6 py-2.5 rounded-full text-xs">
            Volver a Artículos
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const getFormattedContent = (contentHtml: string) => {
    if (preferences.bionic) {
      return toBionicHtml(contentHtml);
    }
    return contentHtml;
  };

  return (
    <>
      <Header />
      <main className="min-h-screen bg-bg-main pt-28 pb-16">
        <article className="max-w-4xl mx-auto px-6 md:px-12">
          {/* HEADER META */}
          <header className="py-12 border-b border-white/5 mb-12">
            <div className="flex items-center gap-2 text-xs text-gold uppercase tracking-widest mb-4">
              <span>{article.fechaFormateada}</span>
              <span>•</span>
              <span className="font-semibold">{article.categoriaId === "filosofia" ? "Filosofía del Poder" : "Psicología Cognitiva"}</span>
              <span>•</span>
              <span>Lectura de {article.lecturaMin} Min</span>
            </div>
            
            <h1 className="font-serif text-3xl md:text-5xl text-text-pure leading-tight tracking-wide font-light uppercase">
              {article.tituloLargo || article.titulo}
            </h1>
            
            <div className="w-12 h-[1.5px] bg-gold/50 my-6"></div>

            {/* AUTHOR DETAILS */}
            <div className="flex items-center gap-4 mt-6">
              <img
                src={article.autor.avatar}
                alt={article.autor.nombre}
                className="w-12 h-12 rounded-full border border-gold/30 object-cover"
              />
              <div>
                <h4 className="text-text-pure text-sm font-semibold">{article.autor.nombre}</h4>
                <p className="text-text-muted text-xs font-light">{article.autor.titulo}</p>
              </div>
            </div>
          </header>

          {/* ARTICLE BODY */}
          <div className="prose prose-invert max-w-none space-y-6 text-sm md:text-base leading-relaxed font-light text-text-pure/90">
            {/* Free content */}
            <div
              className="space-y-6"
              dangerouslySetInnerHTML={{ __html: getFormattedContent(article.contenidoGratis) }}
            />

            {/* Premium Content or Paywall */}
            {user ? (
              <div
                className="space-y-6 pt-6 border-t border-white/5 animate-fade-in"
                dangerouslySetInnerHTML={{ __html: getFormattedContent(article.contenidoPremium) }}
              />
            ) : (
              <div className="relative mt-12 pt-12 border-t border-white/5">
                {/* Blurred premium preview */}
                <div className="opacity-15 blur-xs select-none pointer-events-none space-y-6">
                  <p>
                    Esta ruptura radical con la tradición escolar y teológica implicaba una profunda y dolorosa reevaluación del lugar que ocupa el ser humano en el cosmos. Hasta la irrupción de Spinoza, y de manera muy marcada y explícita en la metafísica de Descartes, el ser humano era considerado un "imperio dentro de otro imperio" (imperium in imperio)...
                  </p>
                  <p>
                    La arquitectura del ser: El Monismo Sustancial y el colapso del dualismo... Para comprender la magnitud abrumadora del determinismo que postula Baruch Spinoza, es metodológicamente imperativo analizar primero la base ontológica fundacional sobre la cual se erige todo su sistema...
                  </p>
                </div>

                {/* Crystal Paywall Card */}
                <div className="absolute inset-0 flex items-center justify-center p-6 bg-gradient-to-t from-bg-main via-bg-main/90 to-transparent">
                  <div className="w-full max-w-md p-8 rounded-2xl border border-white/10 bg-bg-secondary/80 backdrop-blur-md text-center shadow-2xl">
                    <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-5 h-5 text-gold animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h3 className="font-serif text-lg text-text-pure mb-2 uppercase tracking-wide">
                      Contenido Exclusivo
                    </h3>
                    <p className="text-text-muted text-xs font-light leading-relaxed mb-6">
                      Inicie sesión gratuitamente para continuar leyendo esta investigación completa.
                    </p>
                    <button
                      onClick={loginWithGoogle}
                      className="btn-gold-solid w-full py-3 rounded-full text-xs font-semibold tracking-widest uppercase transition-all hover:shadow-[0_0_15px_rgba(197,160,89,0.4)]"
                    >
                      Iniciar Sesión con Google
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
